import { IntervalDateEnum } from '@/types';
import {
  SpmFilterOperator,
  SpmFilterType,
  FILTER_OPERATORS_BY_TYPE,
  DEFAULT_OPERATOR_BY_TYPE,
  isRangeOperator,
} from './operators';
import {
  SpmFilterConfig,
  SpmActiveFilter,
  SpmApiFilter,
  DateRangeFilterValue,
} from './types';

/**
 * Get the filter type from a filter config, defaulting to 'textInput'
 */
export function getFilterType(filterConfig?: SpmFilterConfig): SpmFilterType {
  return filterConfig?.type || 'textInput';
}

/**
 * Get available operators for a filter config
 * Uses allowedOperators if defined, otherwise uses default operators for the filter type
 */
export function getAvailableOperators(filterConfig?: SpmFilterConfig): SpmFilterOperator[] {
  if (filterConfig?.allowedOperators?.length) {
    return filterConfig.allowedOperators;
  }

  const filterType = getFilterType(filterConfig);
  return FILTER_OPERATORS_BY_TYPE[filterType] || FILTER_OPERATORS_BY_TYPE.textInput;
}

/**
 * Get the default operator for a filter config
 * Uses defaultOperator if defined, otherwise uses default for the filter type
 */
export function getDefaultOperator(filterConfig?: SpmFilterConfig): SpmFilterOperator {
  if (filterConfig?.defaultOperator) {
    return filterConfig.defaultOperator;
  }

  const filterType = getFilterType(filterConfig);
  return DEFAULT_OPERATOR_BY_TYPE[filterType];
}

/**
 * Check if a value is a DateRangeFilterValue
 */
export function isDateRangeFilterValue(value: unknown): value is DateRangeFilterValue {
  return (
    value !== null
    && typeof value === 'object'
    && 'interval' in (value as object)
  );
}

/**
 * Check if a filter value is empty/invalid
 */
export function isFilterEmpty(filter: SpmActiveFilter): boolean {
  const { operator, values } = filter;

  // Null or undefined
  if (values === undefined || values === null) {
    return true;
  }

  // Empty string
  if (values === '') {
    return true;
  }

  // DateRangeFilterValue check - empty if interval is 'empty'
  if (typeof values === 'object' && values !== null && !Array.isArray(values) && 'interval' in values) {
    return (values as DateRangeFilterValue).interval === 'empty';
  }

  // Array checks
  if (Array.isArray(values)) {
    // Empty array
    if (values.length === 0) {
      return true;
    }

    // For BETWEEN operators, need exactly 2 non-null values
    if (isRangeOperator(operator)) {
      return values.length < 2 || values[0] == null || values[1] == null;
    }
  }

  return false;
}

/**
 * Compute date range from an IntervalDateEnum
 * Returns [startDate, endDate] as ISO strings
 */
export function computeDateRangeFromInterval(interval: IntervalDateEnum | 'empty' | string, customDateRange?: Date[]): [string, string] | null {
  const now = new Date();
  let startDate: Date;
  let endDate: Date = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

  switch (interval) {
    case 'empty':
      return null;

    case IntervalDateEnum.LAST_DAY:
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 1);
      startDate.setHours(0, 0, 0, 0);
      break;

    case IntervalDateEnum.LAST_3_DAYS:
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 3);
      startDate.setHours(0, 0, 0, 0);
      break;

    case IntervalDateEnum.LAST_7_DAYS:
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 7);
      startDate.setHours(0, 0, 0, 0);
      break;

    case IntervalDateEnum.LAST_14_DAYS:
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 14);
      startDate.setHours(0, 0, 0, 0);
      break;

    case IntervalDateEnum.LAST_30_DAYS:
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 30);
      startDate.setHours(0, 0, 0, 0);
      break;

    case IntervalDateEnum.LAST_3_MONTHS:
      startDate = new Date(now);
      startDate.setMonth(startDate.getMonth() - 3);
      startDate.setHours(0, 0, 0, 0);
      break;

    case IntervalDateEnum.LAST_12_MONTHS:
      startDate = new Date(now);
      startDate.setFullYear(startDate.getFullYear() - 1);
      startDate.setHours(0, 0, 0, 0);
      break;

    case IntervalDateEnum.CUSTOM_DATE_RANGE:
      if (customDateRange && customDateRange.length >= 2 && customDateRange[0] && customDateRange[1]) {
        startDate = new Date(customDateRange[0]);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(customDateRange[1]);
        endDate.setHours(23, 59, 59, 999);
      } else {
        return null;
      }
      break;

    default:
      return null;
  }

  return [startDate.toISOString(), endDate.toISOString()];
}

/**
 * Convert DateRangeFilterValue to API format [startDate, endDate]
 */
export function convertDateRangeToApiValues(value: DateRangeFilterValue): [string, string] | null {
  return computeDateRangeFromInterval(value.interval, value.customDateRange);
}

/**
 * Build API filters from active filters and persistent filters
 * User filters override persistent filters on the same field
 */
export function buildApiFilters(
  activeFilters: SpmActiveFilter[],
  persistentFilters: SpmApiFilter[] = [],
): SpmApiFilter[] {
  const userFilters: SpmApiFilter[] = [];

  // Convert active filters to API format, filtering out empty ones
  activeFilters
    .filter((f) => !isFilterEmpty(f))
    .forEach((f) => {
      // Handle dateRange filter type - convert DateRangeFilterValue to [startDate, endDate]
      if (isDateRangeFilterValue(f.values)) {
        const dateRange = convertDateRangeToApiValues(f.values);
        if (dateRange) {
          userFilters.push({
            field: f.field,
            operator: SpmFilterOperator.BETWEEN,
            values: dateRange,
            relationFilter: f.relationFilter,
          });
        }
        return;
      }

      userFilters.push({
        field: f.field,
        operator: f.operator,
        values: f.values,
        relationFilter: f.relationFilter,
      });
    });

  // Keep persistent filters that are not overridden by user filters
  const persistentFiltered = persistentFilters.filter(
    (pf) => !userFilters.some((uf) => uf.field === pf.field),
  );

  return [...persistentFiltered, ...userFilters];
}

/**
 * Create an empty/default active filter for a field
 */
export function createDefaultFilter(
  fieldName: string,
  filterConfig?: SpmFilterConfig,
): SpmActiveFilter {
  const operator = getDefaultOperator(filterConfig);
  const filterType = getFilterType(filterConfig);

  // Determine default value based on filter type
  let defaultValue: unknown = '';

  if (filterType === 'multiSelect' || filterType === 'languageSelect' || filterType === 'countrySelect') {
    defaultValue = [];
  } else if (filterType === 'dateRange') {
    defaultValue = null;
  } else if (filterType === 'numberInput') {
    defaultValue = null;
  }

  return {
    field: fieldName,
    operator,
    values: defaultValue,
  };
}

/**
 * Reset a filter to its default state
 */
export function resetFilter(filter: SpmActiveFilter, filterConfig?: SpmFilterConfig): SpmActiveFilter {
  return createDefaultFilter(filter.field, filterConfig);
}

/**
 * Check if a filter has been modified from its default state
 */
export function isFilterModified(filter: SpmActiveFilter, filterConfig?: SpmFilterConfig): boolean {
  const defaultFilter = createDefaultFilter(filter.field, filterConfig);

  // Check operator
  if (filter.operator !== defaultFilter.operator) {
    return true;
  }

  // Check values
  if (Array.isArray(filter.values) && Array.isArray(defaultFilter.values)) {
    return filter.values.length !== defaultFilter.values.length
      || filter.values.some((v, i) => v !== (defaultFilter.values as unknown[])[i]);
  }

  return filter.values !== defaultFilter.values;
}
